// ============================================================
// AYASA — the live shop card
//
// Turns every model card whose MODELS entry carries a `pan` block into the
// playable instrument the studio designed: the photographed pan (or the drawing)
// with an invisible touch layer, the flip to the bottom side, the play runs, the
// show-notes toggle, and load-on-first-touch audio. Cards without a pan block —
// models with no built geometry yet — keep the static note-map untouched.
//
// This module is deliberately dumb. Everything it needs is IN the entry, put
// there by the studio's writer: the definition URL with its build stamp, the
// renders with capture stamps, the RESOLVED sample URL per note, the resolved
// preset. No resolver, no manifest, no guessing — the intelligence stays in the
// studio, where it can be fixed without a theme push.
//
// Ported from the studio's card preview (studio/ui/card-preview.html), which
// remains the reference implementation the Shop card tab shows.
// ============================================================
import { createPan2D, keyboardMap } from "./pan2d.js?v=153";

// the shop's words in the page's language (snippets/ayasa-i18n.liquid); the
// Studio's card lab has no shop texts and keeps the English written here
const tx = (key, en, vars) => {
  const k = "js." + key;
  const s = typeof window !== "undefined" && window.AYASA_T && k in window.AYASA_T ? window.AYASA_T[k] : en;
  return vars ? s.replace(/\{(\w+)\}/g, (all, v) => (v in vars ? String(vars[v]) : all)) : s;
};

const CARDS = typeof MODELS !== "undefined" ? MODELS.filter(m => m.pan) : [];

function init() {
  const byHandle = new Map(), byName = new Map();
  for (const m of CARDS) {
    if (m.productHandle) byHandle.set(m.productHandle, m);
    byName.set(m.name, m);
  }

  // the models-page grid, the product page's "keep exploring" cards, the artist
  // rails — every surface that renders modelCard()
  document.querySelectorAll(".model-card").forEach(card => {
    const handle = card.querySelector(".shortlist-heart")?.dataset.handle
      || (card.querySelector(".model-link")?.getAttribute("href") || "").split("/products/")[1];
    const name = card.querySelector("h3")?.textContent?.trim();
    const m = (handle && byHandle.get(handle)) || (name && byName.get(name));
    if (!m || card.dataset.panMounted) return;
    card.dataset.panMounted = "1";
    const slot = takeNotemapSlot(card.querySelector(".notemap"));
    if (!slot) return;
    // a homepage card takes its final height now, not when its definition
    // lands: on mobile data that is seconds later, and every card above the
    // reader grew under them (the grid has no scroll anchoring) and the page
    // leapt down. Only #range: the shop page's cards size themselves.
    if (card.closest("#range")) {
      card.classList.add("pan-pending");
      const boxH = typeof AYASA_CFG !== "undefined" && AYASA_CFG.cardHeight;
      if (boxH) card.style.setProperty("--card-h", boxH + "px");
    }
    mountCard(card, slot, card.querySelector(".notemap-bottom"), m);
  });

  // the product page's compact notes box, between price and description;
  // the funnel templates (section.ayf) carry the same box
  const section = document.querySelector(".product-section[data-handle], .ayf[data-handle]");
  // a condition page ({base}-scratched / -spotted) is the same instrument —
  // fall back to the base handle so the 3D player never vanishes
  const m = section && (byHandle.get(section.dataset.handle)
    || byHandle.get(section.dataset.handle.replace(/-(scratched|spotted)$/, "")));
  // the photo viewer's 3D chip enters here, carrying the photo's angle
  if (m && m.pan) {
    window.AYASA_OPEN_FULL_PLAYER = async startAngle => {
      const def = await fetch(m.pan.def).then(r => r.ok ? r.json() : null).catch(() => null);
      if (def) openFullPlayer(m, def, startAngle);
    };
    // the notes box used to carry no Full Player button by design; Roy asked
    // for one on 28-08 — same pill as the range cards, under the pan
    const notesBox = document.getElementById("productNotes");
    if (notesBox && !notesBox.querySelector(".fp-btn")) {
      const b = document.createElement("button");
      b.type = "button";
      b.className = "hear-btn fp-btn fp-btn-notes";
      b.innerHTML = `<span class="hear-play" aria-hidden="true">3D</span>Full Player`;
      b.setAttribute("aria-label", tx("cards.open_full_player", "open the full 2D/3D player"));
      b.addEventListener("click", () => window.AYASA_OPEN_FULL_PLAYER(null));
      notesBox.appendChild(b);
    }
    // a link can open the player straight away: /products/<handle>?player=3d
    // (Roy 29-10, the newsletter's "Try the 3D player" button)
    if (new URLSearchParams(location.search).get("player") === "3d") window.AYASA_OPEN_FULL_PLAYER(null);
  }
  const box = document.getElementById("productNotes");
  if (m && box) {
    const slot = takeNotemapSlot(box.querySelector(".notemap"));
    if (slot) {
      mountCard(box, slot, document.getElementById("productNoteBottom"), m);
      paintNotesPhoto(box, m);
      box.hidden = false;
    }
  }
}

// The notes box may wear the same photograph the card wears. It is a very
// different shape — nearly square where the card is tall — so the same
// picture is framed again for it: `cover` fills the box, and the studio's
// zoom and offsets choose which part of it shows. Whether it appears at
// all, and how strongly it sits under the text, is one answer for the whole
// shop (Settings -> shop cards), not a per-model decision.
function paintNotesPhoto(box, m) {
  const cfg = (typeof AYASA_CFG !== "undefined" && AYASA_CFG.notesBox) || {};
  const old = box.querySelector(".notes-bg");
  if (old) old.remove();
  if (!cfg.show || !m.cardPhoto) return;
  // photoBox rides beside cardPhoto on the model, where the framing belongs
  const b = m.photoBox || {};
  const zoom = Math.max(100, Math.min(250, Number(b.zoom) || 100));
  const bg = document.createElement("div");
  bg.className = "notes-bg" + (cfg.treatment === "even" ? " even" : " fade");
  bg.style.backgroundImage = `url("${m.cardPhoto}")`;
  bg.style.backgroundSize = zoom === 100 ? "cover" : `auto ${zoom}%`;
  bg.style.backgroundPosition = `${Number(b.x) ?? 50}% ${Number(b.y) ?? 50}%`;
  box.classList.add("has-notes-bg");
  box.prepend(bg);
}

// the player takes the spot the static note-map held; the bottom sentence stays
function takeNotemapSlot(notemap) {
  if (!notemap) return null;
  notemap.classList.add("pan-notemap");
  notemap.querySelector("svg")?.remove();
  const slot = document.createElement("div");
  slot.className = "pan-slot";
  notemap.prepend(slot);
  return slot;
}

// ---------- sound ----------
// The card plays through pan-audio — the SAME engine the product page and the
// Full Player use. It used to have its own copy of the strike voicing, kept in
// step by hand, and the two drifted exactly as you would expect: the card grew
// a limiter the players never had, and once a sample set arrived normalised to
// full scale that limiter caught every note, squashing each attack and leaving
// the tail lifted about 2 dB while the room noise rose into it. One engine
// cannot drift from itself.
//
// Still loaded on the FIRST touch and not before — a grid of thirty cards pays
// nothing for audio nobody asked for. pan-audio shares one AudioContext across
// everything on the page, which is what the card's own bus was for.
const engines = new Map();
const CFG = () => (typeof AYASA_CFG !== "undefined" && AYASA_CFG) || {};

function engineFor(m, def) {
  let e = engines.get(m);
  if (e) return e;
  // the shop serves these FLAT, so the engine's logical keys are translated
  // here exactly as the Full Player translates them
  const resolveAsset = key => {
    let x;
    if ((x = /^samples\/([^/]+)\/(.+)\.mp3$/.exec(key)))
      return AYASA_ASSET(`smp-${x[1]}-${x[2].replace("#", "s")}.mp3`);
    if (key === "samples/index.json") return m.pan.smpIndex || AYASA_ASSET("smp-index.json");
    return AYASA_ASSET(key.split("/").pop());
  };
  let audio = null, loading = null;
  const ensure = () => (loading ||= (async () => {
    const { createPanAudio } = await import("./pan-audio.js?v=153");
    const snd = CFG().sound || {};
    audio = createPanAudio(def, "", resolveAsset, m.pan.samples, {
      // The WHOLE settings object, spread. Naming the fields one at a time is
      // how the card ended up voiced differently from the Full Player beside it
      // — four settings had been added since and none of them were on this
      // line, so the card quietly kept the engine's defaults for all four. A
      // spread cannot fall behind.
      ...snd,
      // measured once in the studio and carried beside the sample urls: where
      // each recording's strike lands, and where it stops being a note
      leadIn: m.pan.leadIn, tail: m.pan.tail
    });
    // Wake it HERE and wait. A context created off a click can start suspended,
    // and its clock does not advance until it resumes — so the first note of a
    // run was scheduled against a stopped clock and sounded whenever the wake
    // finally landed, while the notes after it were already running on the wall
    // clock. That is the squashed gap between the first two notes.
    try { await audio.resume(); } catch { /* it plays when the browser allows */ }
    await audio.load();
  })().catch(() => {}));
  e = {
    ensure,
    strike(n, when = 0) {
      if (audio) return audio.strike(n, when);
      ensure().then(() => audio && audio.strike(n, when));
    },
    damp: n => audio && audio.damp(n),
    panic: () => audio && audio.panic(),
    // how long after its scheduled time a note leaves the speakers (pan-audio
    // reads outputLatency): a few ms on a laptop, 150-250 ms over Bluetooth
    latency: () => audio?.latency?.() || 0
  };
  engines.set(m, e);
  return e;
}

// one run anywhere, ever — starting a run stops whatever else is sounding
let currentRun = null;

// ---------- the keyboard ----------
// A grid can hold thirty cards and there is one keyboard, so a key plays the
// card you last touched and nothing else — claimed on any press inside a
// player. While the Full Player is open it owns the keys instead: the viewer
// binds its own, and both answering would strike every note twice.
let keyOwner = null;
addEventListener("keydown", e => {
  if (fpOverlay || !keyOwner || e.repeat || e.metaKey || e.ctrlKey || e.altKey) return;
  const t = e.target;
  if (t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName))) return;
  const note = keyOwner.keys[e.key && e.key.toLowerCase()];
  if (!note) return;
  e.preventDefault();
  keyOwner.strike(note);
});
function stopRun() {
  currentRun?.stop();
  currentRun = null;
}

// ---------- the Full 2D/3D Player ----------
// One module, full-player.js, opens it here and in the studio's 3D Viewer
// twin, so the two can never drift: the live instrument, its 2D drawing behind
// a 2D/3D switch, and the panel the studio composes. This side hands it the
// shop's plumbing: the flat asset names the writer synced, the very sample map
// the card plays, the card's voicing, and AYASA_CFG.fullPlayer. Loaded only
// when the button is pressed; three.js costs nothing until then.
let fpOverlay = null;
async function openFullPlayer(m, def, startAngle) {
  if (fpOverlay) return;
  stopRun();                               // one player at a time, always
  fpOverlay = true;                        // the keys are the player's from here

  const resolveAsset = key => {
    let x;
    if ((x = /^samples\/([^/]+)\/(.+)\.mp3$/.exec(key)))
      return AYASA_ASSET(`smp-${x[1]}-${x[2].replace("#", "s")}.mp3`);
    if (key === "samples/index.json")
      return m.pan.smpIndex || AYASA_ASSET("smp-index.json");
    if (/^models3d\/.+\.model\.json$/.test(key))
      return m.pan.def;                    // already carries its build stamp
    return AYASA_ASSET(key.split("/").pop());
  };

  try {
    const { openFullPlayer: open } = await import("./full-player.js?v=153");
    await open({
      title: m.name,
      // Hand it the very map the card plays, rather than letting it deduce the
      // same answer from the sample index: the deduction is what fell back to
      // the synthesised set while the card beside it played the real recordings.
      viewer: {
        def, resolveAsset, samples: m.pan.samples,
        // the card and the player it opens from must not be voiced differently
        sound: { ...(CFG().sound || {}), leadIn: m.pan.leadIn, tail: m.pan.tail }
      },
      // the look's rim shapes the mesh; full-player builds it from the look
      look: m.pan.look || null,
      panel: CFG().fullPlayer || null,
      // arriving from a tagged photo: start the camera at that photo's angle
      startAngle,
      onClose: () => { fpOverlay = null; }
    });
  } catch (e) {
    fpOverlay = null;
    console.warn("the Full Player could not open:", e);
  }
}

const SEMI = { C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11 };
const midiOf = n => {
  const m = /^([A-G])(#|b)?([0-8])$/.exec(n);
  return m ? SEMI[m[1]] + (m[2] === "#" ? 1 : m[2] === "b" ? -1 : 0) + (+m[3] + 1) * 12 : null;
};

// The bottom-note dots that come out with show notes: their size, colour and
// name size, from the card's preset. Scoped to this one layer, so two presets
// on one page never paint each other's dots, and set in px on the dot itself —
// never as a scale on the figure, which the instrument-size slider already
// scales. At the defaults nothing is emitted and the stylesheet's own amber
// 9px dots stand. The same function lives in the studio's card-preview.html.
function styleDots(layer, p) {
  const size = Number(p.dotSize), hue = p.dotHue, txt = Number(p.textSize);
  // yellow is the stylesheet's own; a preset from before the choice existed
  // counts as "own colour" when it carries a hue
  const mode = p.dotMode || (hue != null ? "hue" : "amber");
  const id = "sd" + Math.random().toString(36).slice(2, 8);
  const S = `.satdots[data-dotstyle="${id}"]`;
  const rules = [];
  if (Number.isFinite(size) && size !== 9) rules.push(`${S} .satdot i { width: ${size}px; height: ${size}px; }`);
  if (mode === "rainbow") {
    // the house rainbow, --anneal, in the dot; warm light off one side and
    // cool off the other, since a glow cannot be a gradient
    const glow = (o, b, s, a) => `-${o}px -${o}px ${b}px ${s}px rgba(217, 164, 91, ${a}), ${o}px ${o}px ${b}px ${s}px rgba(63, 91, 143, ${a})`;
    rules.push(`${S} .satdot i { background: linear-gradient(135deg, #d9a45b 0%, #c96a3b 38%, #7a4a63 68%, #3f5b8f 100%); box-shadow: ${glow(2, 9, 2, 0.7)}; }`,
      `.pan-slot.labels-on ${S} .satdot i { box-shadow: ${glow(1, 5, 1, 0.45)}; }`,
      `${S} .satdot.flash i, .pan-slot.labels-on ${S} .satdot.flash i { box-shadow: ${glow(3, 12, 3, 0.85)}; }`);
  } else if (mode === "hue" && hue != null && Number.isFinite(Number(hue))) {
    const c = a => `hsla(${Number(hue)}, 73%, 66%, ${a})`;
    rules.push(`${S} .satdot i { background: ${c(1)}; box-shadow: 0 0 10px 3px ${c(0.75)}; }`,
      `.pan-slot.labels-on ${S} .satdot i { box-shadow: 0 0 6px 1px ${c(0.45)}; }`,
      `${S} .satdot.flash i, .pan-slot.labels-on ${S} .satdot.flash i { box-shadow: 0 0 12px 4px ${c(0.85)}; }`);
  }
  if (Number.isFinite(txt) && txt !== 100) rules.push(`${S} .satdot span { font-size: ${(0.55 * txt / 100).toFixed(3)}rem; }`);
  if (!rules.length) return;
  layer.dataset.dotstyle = id;
  const st = document.createElement("style");
  st.textContent = rules.join("\n");
  layer.appendChild(st);
}

async function mountCard(card, slot, bottomEl, m) {
  const preset = m.pan.preset;
  const def = await fetch(m.pan.def).then(r => r.ok ? r.json() : null).catch(() => null);
  if (!def) { card.classList.remove("pan-pending"); return; } // the static map was already removed; fail quiet and small
  const engine = engineFor(m, def);       // needs the definition, so not before now
  card.classList.remove("pan-pending");
  card.classList.add("pan-card");
  // the box the studio chose, not a number mirrored into the stylesheet
  const boxH = typeof AYASA_CFG !== "undefined" && AYASA_CFG.cardHeight;
  if (boxH) card.style.setProperty("--card-h", boxH + "px");
  card.style.setProperty("--panw", preset.panWidth + "rem");
  card.style.setProperty("--btno", (preset.btnOpacity ?? 55) / 100);

  const TOP = def.fields.filter(f => f.side === "top").map(f => f.note);
  const BOTTOM = def.fields.filter(f => f.side === "bottom").map(f => f.note);
  const R = m.pan.render;
  const canFlip = !!(preset.flip && R?.bottom?.src && BOTTOM.length);
  const satellites = preset.underside && !canFlip;

  const strike = n => { pan.highlight(n); backPan?.highlight(n); engine.strike(n); };
  // a finger that slid off the note it woke — the card is most of a phone
  // screen, so a scroll very often starts on a tone field. The note goes
  // quiet under the hand rather than ringing on from a touch that left.
  const damp = n => engine.damp(n);
  // the instrument's own layout: g the ding, b the lowest top note, the ring
  // climbing round it — the same map the 3D viewer plays (pan2d's keyboardMap)
  let KEYS = {};
  try { KEYS = keyboardMap(def); } catch { KEYS = {}; }
  const claimKeys = () => { keyOwner = { keys: KEYS, strike }; };

  const build = view => {
    const isBottom = view === "bottom";
    const shown = isBottom || satellites
      ? def
      : { ...def, fields: def.fields.filter(f => f.side === "top") };
    const face = isBottom ? R?.bottom : R;
    const asPhoto = preset.picture === "render" && !!face?.src;
    return createPan2D(shown, {
      style: preset.style,
      showLabels: false,                  // pan2d's own strike-flash mode
      rotate: 90,
      view,
      picture: !asPhoto,
      renderSrc: asPhoto ? face.src : null,
      renderRadius: face?.radius || 0,
      renderSpin: face?.spin || 0,
      noteLight: preset.noteLight,        // Played note: current glow / note map
      textSize: (preset.textSize ?? 100) / 100,
      drawSec: preset.nmDraw, nameSec: preset.nmNames, lineW: preset.nmLine,
      nameAt: (preset.nmNamesAt ?? 100) / 100,
      drawLines: preset.nmLines !== false, namesAnim: preset.nmNamesFade !== false,
      nameHold: preset.nameHold, lineHold: preset.lineHold,
      onNote: strike,
      onDamp: damp
    });
  };

  const pan = build("top");
  let backPan = null;

  const fig = document.createElement("div");
  fig.className = "pan-figure";
  fig.addEventListener("pointerdown", claimKeys);   // this card takes the keys

  // the bottom notes' places around the rim, for the both-sides run
  // ---- the bottom notes' lights, in a ring just outside the flange ----
  // Placed in the DRAWING's own coordinates, not as a percentage of the
  // figure's box: that box is a bounding rectangle, so a fixed percentage
  // traced an ellipse and the pan's centre was not its centre. One note
  // would sit clear of the rim and its neighbour halfway up the flange.
  // Here every dot sits on one true circle around the pan, at a radius just
  // past whichever edge is actually drawn — the render's flange when the
  // card wears a photograph, the rim when it wears the drawing.
  const satDots = {};
  let satLayer = null;
  if (BOTTOM.length && !satellites) {
    satLayer = document.createElement("div");
    satLayer.className = "satdots";
    // createPan2D hands back the <svg> ITSELF, so looking for one inside it
    // finds nothing and the ring quietly fell back to the rim
    const svgEl = pan.el?.tagName?.toLowerCase() === "svg" ? pan.el : pan.el?.querySelector("svg");
    const vb = svgEl?.viewBox?.baseVal;
    // The radius comes from the DRAWING's own extent, not from the rim: the
    // photograph is drawn out to the flange, which is wider than the rim, so
    // a ring sized off the rim sat on the flange instead of around it. The
    // viewBox already bounds everything drawn, and it needs no render to
    // have arrived first — which the rim-based version did, and did not get.
    // Settings → shop cards: the ring's size in percent, 100 = where it has
    // always sat. Dots and their names move out together along their own angle.
    const ringK = Number.isFinite(+preset.dotRing) && +preset.dotRing > 0 ? +preset.dotRing / 100 : 1;
    const ringR = (vb ? vb.width / 2 : (def.meta?.rimRadius || 260)) * 0.97 * ringK;
    for (const f of def.fields) {
      if (f.side !== "bottom" || !f.boundary) continue;
      let cx = 0, cy = 0;
      f.boundary.forEach(([x, y]) => { cx += x; cy += y; });
      cx /= f.boundary.length; cy /= f.boundary.length;
      const sx = cy, sy = cx;             // CAD (x,y) lands on screen (y,x)
      const len = Math.hypot(sx, sy) || 1;
      const px = ringR * sx / len, py = ringR * sy / len;
      const d = document.createElement("div");
      d.className = "satdot";
      if (vb && vb.width && vb.height) {
        d.style.left = ((px - vb.x) / vb.width * 100) + "%";
        d.style.top = ((py - vb.y) / vb.height * 100) + "%";
      } else {                            // no drawing to measure against
        d.style.left = (50 + 46 * ringK * sx / len) + "%";
        d.style.top = (50 + 46 * ringK * sy / len) + "%";
      }
      d.innerHTML = `<i></i><span>${f.note}</span>`;
      satLayer.appendChild(d);
      satDots[f.note] = d;
    }
  }
  const flashSat = note => {
    const d = satDots[note];
    if (!d) return;
    d.classList.add("flash");
    const hold = Number(preset.nameHold);
    if (Number.isFinite(hold) && Math.abs(hold - 0.55) > 0.001) {
      clearTimeout(d.__hold);
      d.__hold = setTimeout(() => d.classList.remove("flash"), hold * 1000 + 200);
    } else setTimeout(() => d.classList.remove("flash"), 750);
  };

  const stage = document.createElement("div");
  stage.className = "pan-flip";
  const front = document.createElement("div");
  front.className = "face";
  front.appendChild(pan.el);
  if (satLayer) styleDots(satLayer, preset);
  if (satLayer) front.appendChild(satLayer);
  stage.appendChild(front);
  fig.appendChild(stage);

  const setNotes = turned => {
    if (!bottomEl) return;
    const list = turned ? TOP : BOTTOM;
    const hide = satellites || !list.length;
    bottomEl.innerHTML = hide ? "" : `<b>${turned ? tx("cards.plus_top", "+{n} top:", { n: list.length }) : tx("cards.plus_bottom", "+{n} bottom:", { n: list.length })}</b> ${list.join(" ")}`;
    bottomEl.hidden = hide;
  };

  // ---- the turn, shared by the ⟲ button and the runs ----
  let back = null, turned = false, busy = false, tag = null, flipBtn = null;
  const ensureBack = () => {
    if (back || !canFlip) return;
    backPan = build("bottom");            // first turn pays for the bottom face
    back = document.createElement("div");
    back.className = "face back";
    back.appendChild(backPan.el);
    stage.appendChild(back);
  };
  const flipMs = () => matchMedia("(prefers-reduced-motion: reduce)").matches ? 0 : 620;
  const setTurned = want => {
    if (!canFlip || want === turned || busy) return 0;
    ensureBack();
    turned = want;
    busy = true;
    // the axis is load-bearing: the bottom was shot as the instrument turned
    // about the VERTICAL axis — any other reads 180 degrees wrong
    stage.classList.toggle("turned", turned);
    flipBtn?.setAttribute("aria-pressed", String(turned));
    if (tag) tag.hidden = !turned;
    setNotes(turned);
    const ms = flipMs();
    setTimeout(() => { busy = false; }, ms);
    return ms;
  };

  if (canFlip) {
    tag = document.createElement("span");
    tag.className = "underside-tag";
    tag.textContent = "bottom side";
    tag.hidden = true;
    fig.appendChild(tag);

    flipBtn = document.createElement("button");
    flipBtn.type = "button";
    flipBtn.className = "flipbtn";
    flipBtn.title = "turn the instrument over";
    flipBtn.setAttribute("aria-label", "turn the instrument over");
    flipBtn.textContent = "⟲";
    flipBtn.addEventListener("click", e => {
      // the card is one big link to the product; turning it over is not a visit
      e.preventDefault();
      e.stopPropagation();
      setTurned(!turned);
    });
    fig.appendChild(flipBtn);
  }
  slot.appendChild(fig);

  // ---- the runs and the show-notes toggle ----
  // Settings -> Shop cards -> play run speed, carried in AYASA_CFG beside the
  // card box. 0.24 is what the cards were built with, so an untouched shop is
  // unchanged.
  const GAP = Math.min(1.2, Math.max(0.08,
    (typeof AYASA_CFG !== "undefined" && Number(AYASA_CFG.runGap)) || 0.24));
  const row = document.createElement("div");
  row.className = "pan-runs";

  let notesShown = preset.labels !== false;
  const noteBtn = document.createElement("button");
  noteBtn.type = "button";
  noteBtn.className = "notesbtn";
  const paintNotesBtn = () => {
    slot.classList.toggle("labels-on", notesShown);
    noteBtn.innerHTML = `<span class="i">♪</span> ${notesShown ? tx("cards.hide_notes", "hide notes") : tx("cards.show_notes", "show notes")}`;
    noteBtn.setAttribute("aria-pressed", String(notesShown));
  };
  noteBtn.addEventListener("click", e => {
    e.preventDefault();
    e.stopPropagation();
    notesShown = !notesShown;
    paintNotesBtn();
  });
  row.appendChild(noteBtn);

  async function playSeq(seq, gap, onDone) {
    await engine.ensure();
    // the lights land when the notes reach the ear, not when they are scheduled
    const lat = engine.latency?.() || 0;
    const timers = [];
    // The SOUND is scheduled on the audio clock, all of it, up front: setTimeout
    // is a suggestion the browser honours when it gets round to it, and every
    // late timer became an uneven step. The visuals stay on setTimeout, where a
    // few milliseconds cost nothing.
    seq.forEach((s, i) => { if (s.note) engine.strike(s.note, i * gap); });
    seq.forEach((s, i) => {
      timers.push(setTimeout(() => { s.fx?.(); }, (i * gap + lat) * 1000));
    });
    timers.push(setTimeout(() => { currentRun = null; onDone?.(); }, (seq.length * gap + 1.2 + lat) * 1000));
    return {
      stop() {
        timers.forEach(clearTimeout);
        engine.panic();
        onDone?.();
      }
    };
  }

  const runBtns = [];
  const resetBtns = () => runBtns.forEach(b => {
    b.classList.remove("playing");
    b.textContent = "▶ " + b.dataset.label;
  });
  const mkRun = (label, plan, gap = GAP) => {
    const b = document.createElement("button");
    b.type = "button";
    b.dataset.label = label;
    b.textContent = "▶ " + label;
    b.addEventListener("click", async e => {
      e.preventDefault();
      e.stopPropagation();
      const mine = b.classList.contains("playing");
      stopRun();
      if (mine) return;
      resetBtns();
      b.classList.add("playing");
      b.textContent = "■ " + label;
      currentRun = await playSeq(await plan(), gap, resetBtns);
    });
    runBtns.push(b);
    row.appendChild(b);
    return b;
  };

  const faceRun = (side, upOnly = false) => {
    const up = def.fields.filter(f => f.side === side && midiOf(f.note) !== null)
      .map(f => f.note).sort((a, b) => midiOf(a) - midiOf(b));
    return upOnly ? up : up.concat(up.slice(0, -1).reverse());
  };
  const fx = (note, bottomFace) => () => (bottomFace ? backPan : pan)?.highlight(note);
  const faceSeq = (side, wait = 0, upOnly = false) => {
    const seq = faceRun(side, upOnly).map(n => ({ note: n, fx: fx(n, side === "bottom") }));
    const rests = Math.ceil(wait / (GAP * 1000));
    return Array.from({ length: rests }, () => ({})).concat(seq);
  };

  if (BOTTOM.length && canFlip) {
    mkRun(tx("cards.top_side", "top side"), async () => {
      const wait = setTurned(false);
      return faceSeq("top", wait);
    });
    mkRun(tx("cards.bottom_side", "bottom side"), async () => {
      const wait = setTurned(true);
      return faceSeq("bottom", wait);
    });
    // the instrument's TRUE scale: every note pitch-sorted, up and down, in top
    // view, each bottom note flashing its light at its real place on the rim
    mkRun(tx("cards.both_sides", "both sides"), async () => {
      const wait = setTurned(false);
      const up = def.fields.filter(f => midiOf(f.note) !== null)
        .map(f => f.note).sort((a, b) => midiOf(a) - midiOf(b));
      const run = up.concat(up.slice(0, -1).reverse());
      const seq = run.map(n => ({ note: n, fx: () => {
        pan.highlight(n);
        if (BOTTOM.includes(n)) flashSat(n);
      } }));
      const rests = Math.ceil(wait / (0.22 * 1000));
      return Array.from({ length: rests }, () => ({})).concat(seq);
    }, 0.22);
  } else if (TOP.length) {
    mkRun(tx("cards.play", "play"), async () => {
      const seq = faceSeq("top", 0);
      return satellites
        ? seq.concat(faceRun("bottom").map(n => ({ note: n, fx: fx(n, false) })))
        : seq;
    });
  }
  paintNotesBtn();
  slot.appendChild(row);
  slot.classList.toggle("beside", preset.runs === "beside");
  setNotes(false);

  // The Full Player pill joins WATCH in the theme's own action row. The
  // product-page notes box is NOT handled here: its pill is added at init,
  // where the photo viewer's 3D chip shares the same opener.
  const actions = card.querySelector(".model-actions");
  if (actions && !actions.querySelector(".fp-btn")) {
    const b = document.createElement("button");
    b.type = "button";
    b.className = "hear-btn fp-btn";       // the theme's own pill, deliberately
    // the icon says 3D so the label doesn't have to
    b.innerHTML = `<span class="hear-play" aria-hidden="true">3D</span>Full Player`;
    b.setAttribute("aria-label", tx("cards.open_full_player", "open the full 2D/3D player"));
    b.addEventListener("click", e => {
      e.preventDefault();
      e.stopPropagation();
      openFullPlayer(m, def);
    });
    actions.appendChild(b);
  }
}

// last on purpose: module-level consts above must exist before the first mount
if (CARDS.length) init();
